import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatsOpen, setMobileCatsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    setMobileOpen(false);
    setMobileCatsOpen(false);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const textColor = scrolled ? "text-foreground-700" : "text-white";
  const logoColor = scrolled ? "text-foreground-900" : "text-white";
  const hoverColor = scrolled ? "hover:text-accent-500" : "hover:text-white/80";

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-sm border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className={`text-lg font-bold font-heading tracking-tight transition-colors duration-300 ${logoColor}`}>
              OOPBuy<span className={scrolled ? "text-accent-500" : "text-accent-300"}>Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textColor} ${hoverColor}`}
            >
              Home
            </Link>
            <button
              onClick={() => scrollToSection("categories")}
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textColor} ${hoverColor}`}
            >
              Categories
            </button>
            <button
              onClick={() => scrollToSection("features")}
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textColor} ${hoverColor}`}
            >
              Features
            </button>
            <button
              onClick={() => scrollToSection("articles-section")}
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textColor} ${hoverColor}`}
            >
              Articles
            </button>
            <Link
              to="/oopbuy-spreadsheet-guide"
              className={`text-sm transition-colors whitespace-nowrap ${textColor} ${hoverColor}`}
            >
              Guide
            </Link>
            <button
              onClick={() => scrollToSection("categories")}
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${textColor} ${hoverColor}`}
            >
              Shopping Guide
            </button>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm px-4 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                scrolled
                  ? "bg-foreground-800 text-white hover:bg-foreground-900"
                  : "bg-white text-foreground-900 hover:bg-accent-50"
              }`}
            >
              OOPBuy Spreadsheet
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${scrolled ? "text-foreground-800" : "text-white"} ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 flex flex-col gap-2">
            <Link
              to="/"
              className="text-sm text-foreground-700 hover:text-accent-500 transition-colors py-2"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-line mr-2 align-middle"></i>Home
            </Link>
            <button
              onClick={() => scrollToSection("categories")}
              className="text-sm text-foreground-700 hover:text-accent-500 transition-colors text-left py-2 cursor-pointer"
            >
              <i className="ri-grid-line mr-2 align-middle"></i>Categories
            </button>
            <button
              onClick={() => scrollToSection("features")}
              className="text-sm text-foreground-700 hover:text-accent-500 transition-colors text-left py-2 cursor-pointer"
            >
              <i className="ri-star-line mr-2 align-middle"></i>Features
            </button>
            <button
              onClick={() => scrollToSection("articles-section")}
              className="text-sm text-foreground-700 hover:text-accent-500 transition-colors text-left py-2 cursor-pointer"
            >
              <i className="ri-article-line mr-2 align-middle"></i>Articles
            </button>
            <Link
              to="/oopbuy-spreadsheet-guide"
              className="text-sm text-foreground-700 hover:text-accent-500 transition-colors py-2"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-open-line mr-2 align-middle"></i>Guide
            </Link>

            {/* Shopping Guide - Mobile */}
            <button
              onClick={() => scrollToSection("categories")}
              className="text-sm text-foreground-700 hover:text-accent-500 transition-colors text-left py-2 cursor-pointer"
            >
              <i className="ri-shopping-bag-line mr-2 align-middle"></i>Shopping Guide
            </button>

            {/* Collapsible Category List */}
            <div>
              <button
                onClick={() => setMobileCatsOpen(!mobileCatsOpen)}
                className="w-full flex items-center justify-between text-sm text-foreground-700 hover:text-accent-500 transition-colors py-2 cursor-pointer"
              >
                <span><i className="ri-list-check mr-2 align-middle"></i>Quick Category Links</span>
                <i className={`text-xs transition-transform ${mobileCatsOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
              </button>
              {mobileCatsOpen && (
                <div className="ml-4 mt-1 mb-2 grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => { setMobileOpen(false); setMobileCatsOpen(false); }}
                      className="text-xs text-foreground-600 hover:text-accent-500 transition-colors py-1.5 whitespace-nowrap cursor-pointer"
                    >
                      <i className={`${cat.icon} mr-1 text-accent-500 align-middle`}></i>{cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-background-200/70 pt-3 mt-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="block text-center text-sm px-4 py-2.5 rounded-full bg-foreground-800 text-white hover:bg-foreground-900 transition-all whitespace-nowrap cursor-pointer"
              >
                OOPBuy Spreadsheet <i className="ri-arrow-right-up-line ml-1 align-middle"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}